<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import { CallToActionButtonInput } from 'dashboard/shared/templates/components';
import {
  WHATSAPP_TEMPLATE_LIMITS,
  WHATSAPP_BUTTON_TYPE_ICONS,
} from '../constants/whatsappTemplateTypes';
import {
  useButtonTypeRules,
  useCallToActionValidation as useSharedCallToActionValidation,
  useFormValidation,
  useVariableValidation,
} from 'dashboard/shared/templates/composables';
import { useWhatsappConfig } from '../composables/useWhatsappConfig';

const props = defineProps({
  buttons: {
    type: Array,
    default: () => [],
  },
  body: {
    type: String,
    default: '',
  },
  footer: {
    type: String,
    default: '',
  },
  bodyExamples: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:buttons',
  'update:bodyExamples',
  'submit',
  'back',
]);

// Regex for extracting variables
const VARIABLE_PATTERN = /\{\{(\d+)\}\}/g;

const { t } = useI18n();

const MAX_CTA_BUTTONS = 4;

// Button type options with translations
const CTA_TYPE_OPTIONS = computed(() => [
  {
    value: 'URL',
    label: t('WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.BUTTON_TYPES.URL'),
    icon: WHATSAPP_BUTTON_TYPE_ICONS.URL,
  },
  {
    value: 'PHONE_NUMBER',
    label: t(
      'WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.BUTTON_TYPES.PHONE_NUMBER'
    ),
    icon: WHATSAPP_BUTTON_TYPE_ICONS.PHONE_NUMBER,
  },
  {
    value: 'COPY_CODE',
    label: t('WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.BUTTON_TYPES.COPY_CODE'),
    icon: WHATSAPP_BUTTON_TYPE_ICONS.COPY_CODE,
  },
  {
    value: 'VOICE_CALL',
    label: t(
      'WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.BUTTON_TYPES.VOICE_CALL'
    ),
    icon: WHATSAPP_BUTTON_TYPE_ICONS.VOICE_CALL,
  },
  {
    value: 'VOICE_CALL_REQUEST',
    label: t(
      'WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.BUTTON_TYPES.VOICE_CALL_REQUEST'
    ),
    icon: WHATSAPP_BUTTON_TYPE_ICONS.VOICE_CALL_REQUEST,
  },
]);

const localButtons = ref(
  props.buttons.length > 0
    ? props.buttons
    : [{ type: 'URL', text: '', url: '' }]
);

watch(
  () => props.buttons,
  newButtons => {
    if (newButtons.length > 0) {
      localButtons.value = newButtons;
    }
  },
  { deep: true }
);

// Extract variables from body
const detectedVariables = computed(() => {
  const matches = props.body.match(VARIABLE_PATTERN) || [];
  return matches
    .map(m => {
      const num = parseInt(m.replace(/[{}]/g, ''), 10);
      return num;
    })
    .filter((v, i, arr) => arr.indexOf(v) === i) // unique
    .sort((a, b) => a - b);
});

const variablesCount = computed(() => {
  if (detectedVariables.value.length === 0) return 0;
  return Math.max(...detectedVariables.value);
});

// Local copy of bodyExamples
const localExamples = ref([...props.bodyExamples]);

// Sync localExamples with props
watch(
  () => props.bodyExamples,
  newVal => {
    localExamples.value = [...newVal];
  },
  { deep: true }
);

// Watch for variable changes and adjust examples array
watch(variablesCount, newCount => {
  const currentLength = localExamples.value.length;

  if (newCount > currentLength) {
    for (let i = currentLength; i < newCount; i += 1) {
      localExamples.value.push('');
    }
  } else if (newCount < currentLength) {
    localExamples.value = localExamples.value.slice(0, newCount);
  }

  emit('update:bodyExamples', localExamples.value);
});

const handleExampleInput = () => {
  emit('update:bodyExamples', localExamples.value);
};

const getVariableLabel = n => `{{${n}}}`;
const getVariablePlaceholder = n => `Example for {{${n}}}`;

// Get WhatsApp-specific config
const { config: whatsappConfig } = useWhatsappConfig();

// Use button type rules composable for mutual exclusivity
const {
  getButtonTypeOptions,
  canAddButton,
  canRemoveButton,
  getFirstAvailableType,
} = useButtonTypeRules(localButtons, CTA_TYPE_OPTIONS, {
  limits: WHATSAPP_TEMPLATE_LIMITS,
  maxButtons: 4,
  minButtons: 1,
});

// Variable validation
const { getUniqueVariables } = useVariableValidation();

// Detect variables from body (passed from previous step)
const bodyVariables = computed(() => {
  return getUniqueVariables(props.body);
});

// Detect variables in button fields based on button type
const detectedButtonVariables = computed(() => {
  const variables = new Set();

  localButtons.value.forEach(button => {
    switch (button.type) {
      case 'URL':
        getUniqueVariables(button.url || '').forEach(v => variables.add(v));
        break;
      case 'COPY_CODE':
        getUniqueVariables(button.example || '').forEach(v => variables.add(v));
        break;
      case 'VOICE_CALL':
        getUniqueVariables(button.text || '').forEach(v => variables.add(v));
        break;
      default:
        break;
    }
  });

  return Array.from(variables).sort(
    (a, b) => parseInt(a, 10) - parseInt(b, 10)
  );
});

// Empty variable validation (WhatsApp CTA step manages its own button validation)
const emptyVariableRules = computed(() => ({}));
const emptyVariableState = computed(() => ({}));

// Use shared Call to Action validation with WhatsApp config
const {
  rules,
  validationState,
  getErrorMessage: getCtaErrorMessage,
} = useSharedCallToActionValidation(
  localButtons,
  bodyVariables,
  detectedButtonVariables,
  emptyVariableRules,
  emptyVariableState,
  {
    i18nPrefix: 'WHATSAPP_TEMPLATES',
    limits: WHATSAPP_TEMPLATE_LIMITS,
    fieldMapping: whatsappConfig.fieldMapping,
    showUrlExample: whatsappConfig.showUrlExample,
    copyCodeShowsText: whatsappConfig.copyCodeShowsText,
  }
);

// Use form validation composable
let v$;
const getErrorMessage = field => getCtaErrorMessage(field, v$);

const formValidation = useFormValidation(
  rules,
  validationState,
  getErrorMessage
);
({ v$ } = formValidation);
const { formErrors } = formValidation;

// Submit is disabled when form is invalid
const isSubmitDisabled = computed(() => v$.value.$invalid);

const emitUpdate = () => {
  emit(
    'update:buttons',
    localButtons.value.map(btn => {
      const cleanBtn = { type: btn.type };

      if (btn.type === 'URL') {
        cleanBtn.text = btn.text;
        cleanBtn.url = btn.url;
        if (btn.url?.includes('{{') && btn.example) {
          cleanBtn.example = btn.example;
        }
      } else if (btn.type === 'PHONE_NUMBER') {
        cleanBtn.text = btn.text;
        cleanBtn.phone_number = btn.phone_number;
      } else if (btn.type === 'COPY_CODE') {
        cleanBtn.example = btn.example;
      } else if (btn.type === 'VOICE_CALL') {
        cleanBtn.text = btn.text;
        cleanBtn.ttl_minutes =
          btn.ttl_minutes || WHATSAPP_TEMPLATE_LIMITS.DEFAULT_TTL_MINUTES;
      } else if (btn.type === 'VOICE_CALL_REQUEST') {
        cleanBtn.text = btn.text;
      }

      return cleanBtn;
    })
  );
};

const addButton = event => {
  event?.stopPropagation();
  if (!canAddButton.value) return;

  const defaultType = getFirstAvailableType();
  if (!defaultType) return;

  const newButton = {
    type: defaultType,
    text: '',
  };

  if (defaultType === 'URL') {
    newButton.url = '';
  } else if (defaultType === 'PHONE_NUMBER') {
    newButton.phone_number = '';
  } else if (defaultType === 'COPY_CODE') {
    newButton.example = '';
  } else if (defaultType === 'VOICE_CALL') {
    newButton.ttl_minutes = WHATSAPP_TEMPLATE_LIMITS.DEFAULT_TTL_MINUTES;
  } else if (defaultType === 'VOICE_CALL_REQUEST') {
    // VOICE_CALL_REQUEST only needs type and text, no additional fields
  }

  localButtons.value.push(newButton);
  emitUpdate();
};

const handleButtonTypeChange = (index, value) => {
  const button = localButtons.value[index];

  // Update the type
  button.type = value;

  // Clear ALL fields when type changes
  button.text = '';
  button.url = '';
  button.phone_number = '';
  button.example = '';
  button.ttl_minutes = WHATSAPP_TEMPLATE_LIMITS.DEFAULT_TTL_MINUTES;

  emitUpdate();
};

const updateButtonField = (index, field, value) => {
  // Update field directly
  localButtons.value[index][field] = value;

  // Touch the field to show validation
  const fieldKey = `button_${index}_${field}`;
  if (v$.value[fieldKey]) {
    v$.value[fieldKey].$touch();
  }

  emitUpdate();
};

const removeButton = index => {
  if (!canRemoveButton.value) return;

  localButtons.value.splice(index, 1);
  emitUpdate();
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  emitUpdate();
  emit('submit');
};

const handleBack = () => emit('back');

// Preview computed
const previewBody = computed(() => {
  let text = props.body;
  props.bodyExamples.forEach((example, index) => {
    text = text.replace(`{{${index + 1}}}`, example || `[${index + 1}]`);
  });
  return text;
});

// Button icon helper
const getButtonIcon = type => {
  const icons = {
    URL: 'i-lucide-external-link',
    PHONE_NUMBER: 'i-lucide-phone',
    COPY_CODE: 'i-lucide-copy',
    VOICE_CALL: 'i-lucide-phone-call',
    VOICE_CALL_REQUEST: 'i-lucide-phone-incoming',
  };
  return icons[type] || 'i-lucide-mouse-pointer-click';
};

// Button preview text
const getButtonPreviewText = (btn, index) => {
  if (btn.type === 'COPY_CODE') {
    return btn.example
      ? `Copy: ${btn.example}`
      : t('WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.PREVIEW_BUTTON', {
          number: index + 1,
        });
  }
  return (
    btn.text ||
    t('WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.PREVIEW_BUTTON', {
      number: index + 1,
    })
  );
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Info message -->
    <div
      class="text-sm p-3 rounded-lg bg-n-blue/10 text-n-slate-12 border border-n-blue/20"
    >
      <div class="font-medium mb-1">
        {{ t('WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.INFO_TITLE') }}
      </div>
      <div class="text-n-slate-11">
        {{ t('WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.INFO_DESCRIPTION') }}
      </div>
    </div>

    <!-- Main content: Form and Preview side by side -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Left side: Form -->
      <div class="flex flex-col gap-4 order-2 lg:order-1">
        <!-- Call to Action Buttons -->
        <CallToActionButtonInput
          v-for="(button, index) in localButtons"
          :key="`button-${index}`"
          :button="button"
          :index="index"
          :type-options="getButtonTypeOptions(index)"
          :config="whatsappConfig"
          :form-errors="formErrors"
          :can-remove="canRemoveButton"
          @update:type="value => handleButtonTypeChange(index, value)"
          @update:field="
            (field, value) => updateButtonField(index, field, value)
          "
          @remove="removeButton(index)"
        />

        <!-- Add button -->
        <button
          v-if="canAddButton"
          type="button"
          class="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-n-slate-7 hover:border-n-blue hover:bg-n-blue/5 text-n-slate-11 hover:text-n-blue transition-colors"
          @click="addButton"
        >
          <i class="i-lucide-plus text-base" />
          <span class="text-sm font-medium">
            {{ t('WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.ADD_BUTTON') }}
          </span>
        </button>

        <!-- Button count info -->
        <div class="text-sm text-n-slate-11">
          {{
            t('WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.BUTTON_COUNT', {
              current: localButtons.length,
              max: MAX_CTA_BUTTONS,
            })
          }}
        </div>

        <!-- Variable Examples -->
        <div v-if="variablesCount > 0" class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_TEMPLATES.CREATE.FORM.EXAMPLES_LABEL') }}
          </label>
          <span class="text-xs text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.CREATE.FORM.EXAMPLES_HINT') }}
          </span>
          <div class="flex flex-col gap-3">
            <div
              v-for="n in variablesCount"
              :key="n"
              class="flex flex-col gap-1"
            >
              <Input
                v-model="localExamples[n - 1]"
                :label="getVariableLabel(n)"
                :placeholder="getVariablePlaceholder(n)"
                :message="!localExamples[n - 1] ? 'Required' : ''"
                :message-type="!localExamples[n - 1] ? 'error' : 'info'"
                @input="handleExampleInput()"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Right side: Phone Preview -->
      <div class="flex flex-col gap-3 order-1 lg:order-2 items-center">
        <h4 class="text-sm font-medium text-n-slate-12 w-full text-center">
          {{ t('WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.PREVIEW_TITLE') }}
        </h4>
        <div
          class="relative w-full max-w-[280px] bg-gradient-to-b from-slate-800 to-slate-900 rounded-3xl p-4 shadow-xl border-4 border-slate-700 aspect-phone"
        >
          <div class="flex flex-col h-full">
            <!-- Phone header -->
            <div class="flex items-center justify-center mb-3">
              <div class="w-12 h-1 rounded-full bg-slate-600" />
            </div>

            <div
              class="flex items-center gap-2 mb-4 pb-2 border-b border-slate-700"
            >
              <div
                class="w-6 h-6 rounded-full bg-slate-600 flex items-center justify-center"
              >
                <i class="i-lucide-user text-xs text-slate-300" />
              </div>
              <span class="text-xs font-medium text-slate-300">
                {{
                  t('WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.PREVIEW_CONTACT')
                }}
              </span>
            </div>

            <!-- Message preview -->
            <div class="flex flex-col gap-2">
              <div
                class="bg-white rounded-2xl rounded-bl-sm p-2.5 shadow-sm max-w-[85%]"
              >
                <!-- Message text -->
                <p
                  class="text-xs text-slate-900 whitespace-pre-wrap break-words"
                >
                  {{
                    previewBody ||
                    t(
                      'WHATSAPP_TEMPLATES.CREATE.CALL_TO_ACTION.PREVIEW_PLACEHOLDER'
                    )
                  }}
                </p>

                <!-- Footer text -->
                <p
                  v-if="footer"
                  class="text-[10px] text-slate-500 mt-2 pt-1 border-t border-slate-100"
                >
                  {{ footer }}
                </p>
              </div>

              <!-- CTA buttons -->
              <div class="flex flex-col gap-1.5">
                <button
                  v-for="(button, index) in localButtons"
                  :key="`preview-button-${index}`"
                  type="button"
                  class="flex items-center justify-center gap-2 bg-white border-2 border-gray-300 rounded-lg px-3 py-2 text-xs font-bold text-center shadow-sm text-[#374151]"
                  disabled
                >
                  <i :class="getButtonIcon(button.type)" class="text-sm" />
                  <span>{{ getButtonPreviewText(button, index) }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex items-center justify-between w-full gap-3 pt-2">
      <Button
        variant="faded"
        color="slate"
        type="button"
        icon="i-lucide-arrow-left"
        :label="t('WHATSAPP_TEMPLATES.CREATE.WIZARD.BACK')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleBack"
      />
      <Button
        :label="t('WHATSAPP_TEMPLATES.CREATE.WIZARD.CREATE')"
        type="button"
        class="w-full"
        :disabled="isSubmitDisabled || loading"
        :is-loading="loading"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>

<style scoped>
.aspect-phone {
  aspect-ratio: 9 / 16;
  min-height: 400px;
}
</style>
